import { auth } from '../firebase/config.js';
import { useContext, useEffect, useRef, useState } from "react";
import UserContext from "../context/UserContext.js";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { Toaster } from '../components/Toast.jsx';
import InactivityHandler from '../components/Inactivity2.jsx';
import { Helmet } from "react-helmet";
import PlanBanner from '../components/PlanBanner.jsx';
import useLandingTheme from '../components/landing/useLandingTheme';
import ThemeToggle from '../components/landing/ThemeToggle';
import QrMark from '../components/landing/QrMark';
import useBusiness, { notify, notifyError } from '../components/panel/useBusiness';
import { finishMpConnect, readMpReturn } from '../mercadoPago.js';
import Icon from '../components/panel/Icon';
import PanelHome from '../components/panel/PanelHome';
import ProductsPanel from '../components/panel/ProductsPanel';
import QrPanel from '../components/panel/QrPanel';
import StatsPanel from '../components/panel/StatsPanel';
import BusinessPanel from '../components/panel/BusinessPanel';
import BusinessForm from '../components/panel/BusinessForm';
import AssistantPanel from '../components/panel/AssistantPanel';
import { PanelSideSkeleton, PanelSkeleton } from '../components/panel/PanelSkeleton';
import '../landing.css';
import '../panel.css';

const TABS = [
  { id: "inicio", label: "Inicio", icon: "home" },
  { id: "productos", label: "Productos", icon: "box" },
  { id: "qr", label: "QR", icon: "qr" },
  { id: "negocio", label: "Mi negocio", short: "Negocio", icon: "store" },
  { id: "estadisticas", label: "Estadísticas", short: "Métricas", icon: "chart" },
  { id: "cuenta", label: "Cuenta", icon: "card" }
];

const tabFromHash = (hash) => TABS.some((t) => t.id === hash.slice(1)) ? hash.slice(1) : "inicio";

const Account = () => {

  const { LSuser, setUser } = useContext(UserContext);
  const { dark, toggleTheme } = useLandingTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const data = useBusiness(LSuser.email);
  const { business, status } = data;

  const tab = tabFromHash(location.hash);
  const [creating, setCreating] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  const goTo = (id) => {
    navigate({ hash: id === "inicio" ? "" : id });
    window.scrollTo(0, 0);
  }

  const handleLogout = () => {
    auth.signOut();
    setUser(null)
    window.localStorage.removeItem("usuario")
    navigate("/login")
  }

  const handleSubscribe = async () => {
    setSubscribing(true);

    try {
      // Workflow "MiQRapp - Suscripciones (Mercado Pago)" en n8n (ver n8n/miqrapp-suscripciones.js).
      const response = await fetch("https://n8n.miqrapp.com/webhook/miqrapp/suscribir", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken: await auth.currentUser.getIdToken() })
      });
      const result = await response.json();

      if (!response.ok || !result.initPoint) {
        throw new Error(result.error || `n8n respondió ${response.status}`);
      }

      window.location.href = result.initPoint;
    } catch (error) {
      console.error(error);
      notifyError("No pudimos iniciar la suscripción. Probá de nuevo en unos minutos.");
      setSubscribing(false);
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (usuarioFirebase) => {
      setUser(usuarioFirebase || null);
    });

    return () => unsubscribe();
  }, [setUser]);

  // Regreso de Mercado Pago después de "Conectar Mercado Pago" (Mi negocio): /cuenta?code=…&state=…
  // Se lee una sola vez y se limpia la dirección para que recargar no lo repita.
  const [mpReturn] = useState(() => readMpReturn(window.location.search));
  useEffect(() => {
    if (!mpReturn) return;
    navigate({ pathname: "/cuenta", search: "", hash: "negocio" }, { replace: true });
  }, [mpReturn, navigate]);

  const reload = data.reload;
  const mpHandled = useRef(false);
  const ready = status === "ready";
  useEffect(() => {
    if (!mpReturn || !ready || mpHandled.current) return;
    mpHandled.current = true;
    if (!mpReturn.valid) {
      notifyError("No se conectó Mercado Pago. Si querés, probá de nuevo desde Mi negocio.");
      return;
    }
    finishMpConnect(mpReturn.code)
      .then((result) => {
        notify(`¡Listo! Conectaste Mercado Pago${result.cuenta ? " (" + result.cuenta + ")" : ""}. Tus clientes ya pueden pagar online.`);
        reload();
      })
      .catch((error) => {
        console.error(error);
        notifyError(error.status === 403 ? "Cobrar con Mercado Pago es del plan Pro." : "No pudimos conectar Mercado Pago. Probá de nuevo en unos minutos.");
      });
  }, [mpReturn, ready, reload]);

  useEffect(() => {
    if (business) {
      window.localStorage.setItem("usuarioActual", JSON.stringify({ sub: business.subscribed, reg: business.registered }))
    }
  }, [business]);

  const renderTab = () => {
    switch (tab) {
      case "productos":
        return <ProductsPanel data={data} creating={creating} setCreating={setCreating}></ProductsPanel>
      case "qr":
        return <QrPanel business={business} onStyle={data.saveQrStyle}></QrPanel>
      case "negocio":
        return <BusinessPanel data={data}></BusinessPanel>
      case "estadisticas":
        return <StatsPanel business={business} products={data.products} goTo={goTo}></StatsPanel>
      case "cuenta":
        return (
          <div className="lp-panel-page">
            <div className="lp-panel-head"><h1>Cuenta</h1></div>
            <div className="lp-panel-grid">
              <PlanBanner subscribed={business.subscribed} subscriptionStatus={business.subscriptionStatus} productCount={data.products.length} subscribing={subscribing} handleSubscribe={handleSubscribe}></PlanBanner>
              <section className="lp-panel-card lp-panel-stack">
                <h2>Tu usuario</h2>
                <dl className="lp-panel-rows">
                  <div><dt>Email</dt><dd>{business.email}</dd></div>
                  <div><dt>Link del menú</dt><dd>miqrapp.com/{business.usuarioEmpresa}</dd></div>
                </dl>
                <Link className="lp-text-link lp-panel-left" to="/preguntas-frecuentes">Preguntas frecuentes</Link>
                <button type="button" className="lp-btn lp-btn-secondary lp-panel-left lp-panel-logout-btn" onClick={handleLogout}><Icon name="logout"></Icon> Cerrar sesión</button>
              </section>
            </div>
          </div>
        )
      default:
        return <PanelHome data={data} goTo={goTo} onAddProduct={() => { goTo("productos"); setCreating(true); }}></PanelHome>
    }
  }

  const renderContent = () => {
    if (status === "loading") {
      return <PanelSkeleton></PanelSkeleton>
    }

    if (status === "error") {
      return (
        <div className="lp-panel-center lp-panel-stack">
          <p>No pudimos cargar tu cuenta. Revisá tu conexión.</p>
          <button type="button" className="lp-btn lp-btn-primary" onClick={data.reload}>Reintentar</button>
        </div>
      )
    }

    if (status === "unregistered") {
      return (
        <div className="lp-panel-page lp-panel-onboarding">
          <div className="lp-panel-head">
            <div>
              <span className="lp-eyebrow">Paso 1 de 2</span>
              <h1>Contanos de tu negocio</h1>
              <p className="lp-panel-muted">Con estos datos armamos tu menú. Tus clientes los van a ver arriba de todo. Después cargás los productos.</p>
            </div>
          </div>
          <section className="lp-panel-card">
            <BusinessForm submitLabel="Crear mi menú" onSubmit={data.register}></BusinessForm>
          </section>
        </div>
      )
    }

    return renderTab();
  }

  return (
    <div className="lp lp-panel" data-theme={dark ? "dark" : "light"}>
      <Helmet>
        <title>Mi cuenta - MiQRapp</title>
        <meta name="robots" content="noindex, nofollow" />
        <body className={dark ? "lp-body lp-body-dark" : "lp-body"} />
        <meta name="theme-color" content={dark ? "#120b14" : "#fbfaf7"} />
      </Helmet>

      <aside className="lp-panel-side">
        <div className="lp-panel-side-top">
          <Link to="/" className="lp-logo">
            <span className="lp-logo-mark"><QrMark></QrMark></span>
            MiQRapp
          </Link>
          <ThemeToggle dark={dark} onToggle={toggleTheme}></ThemeToggle>
        </div>

        {status === "loading" && <PanelSideSkeleton></PanelSideSkeleton>}

        {ready &&
          <div className="lp-panel-side-biz">
            {business.logo ? <img src={business.logo} alt=""/> : <span className="lp-panel-logo-empty" aria-hidden="true"></span>}
            <div>
              <strong>{business.nombreEmpresa}</strong>
              <span className={business.subscribed ? "lp-panel-badge lp-panel-badge-ok" : "lp-panel-badge"}>{business.subscribed ? "Pro activo" : "Plan Gratis"}</span>
            </div>
          </div>
        }

        {ready &&
          <nav className="lp-panel-nav" aria-label="Secciones del panel">
            {TABS.map((t) => (
              <button key={t.id} type="button" aria-current={tab === t.id ? "page" : undefined} onClick={() => goTo(t.id)}>
                <Icon name={t.icon}></Icon>
                {/* En el celular las pestañas van abajo y algunas llevan un nombre más corto. */}
                <span className={t.short ? "lp-panel-nav-long" : undefined}>{t.label}</span>
                {t.short && <span className="lp-panel-nav-short">{t.short}</span>}
              </button>
            ))}
          </nav>
        }

        <button type="button" className="lp-panel-logout" onClick={handleLogout}><Icon name="logout"></Icon> Cerrar sesión</button>
      </aside>

      <main className="lp-panel-main">
        {renderContent()}
      </main>

      {ready && <AssistantPanel data={data}></AssistantPanel>}

      <Toaster></Toaster>

      <InactivityHandler></InactivityHandler>
    </div>
  )
}

export default Account
